import { Alert } from '@mui/material';
import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ContentWrapper } from '@/components/ContentWrapper';
import { PrimaryButton } from '@/components/shared/Button';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { HouseAccountAiiaCandidateAccount } from '@/services/PaymentsServiceV2';
import {
    HouseAccountAiiaAccountPicker,
    HouseAccountAiiaAccountPickerSkeleton
} from './HouseAccountAiiaAccountPicker';

// The return target is hardcoded on purpose: an attacker-controlled redirect
// must never be read from a query parameter (open-redirect protection).
const MONITORING_PAGE_ROUTE = '/transaction-monitoring/page/0';

interface PendingSelection {
    selectionToken: string;
    candidateAccounts: HouseAccountAiiaCandidateAccount[];
}

/**
 * Operator callback for the house-account Aiia connect / re-authorize flow.
 * Reads code/consentId/state from the redirect, exchanges them, then returns to
 * the monitoring page. The sensitive params are stripped from the URL
 * immediately and are never persisted client-side.
 *
 * A consent covering several bank accounts binds nothing on exchange and comes
 * back with the candidates instead, so the flow pauses on a picker here until
 * the operator names the account to bind.
 */
export const HouseAccountAiiaConnectCallbackPage = () => {
    const { t } = useTranslation();
    const [error, setError] = useState<string>();
    const [pendingSelection, setPendingSelection] =
        useState<PendingSelection>();
    const [selectionError, setSelectionError] = useState<string>();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const { exchangeHouseAccountAiia, selectHouseAccountAiiaAccount } =
        usePaymentsMutations();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // The redirect fires once; guard against a re-run replaying the same code.
    const hasExchangedRef = useRef(false);

    const completeConnection = () => {
        setAlert(t('transaction_monitoring.connect_success'), 'success');
        navigate(MONITORING_PAGE_ROUTE);
    };

    useEffect(() => {
        if (hasExchangedRef.current) {
            return;
        }
        hasExchangedRef.current = true;

        const code = searchParams.get('code') ?? '';
        const consentId = searchParams.get('consentId') ?? '';
        const state = searchParams.get('state') ?? '';

        // Strip code/state/consentId from the address bar so they are not left in
        // browser history.
        window.history.replaceState({}, '', window.location.pathname);

        if (!code || !state) {
            setError(t('transaction_monitoring.connect_failed_or_cancelled'));
            return;
        }

        // searchParams.get already URL-decodes the values, so they are passed
        // through as-is (no second decode).
        exchangeHouseAccountAiia.mutate(
            {
                code,
                consentId,
                state
            },
            {
                onSuccess: (response) => {
                    const { selectionToken, candidateAccounts } = response.data;

                    // A selection token is the backend saying "nothing was bound
                    // yet", so it decides the branch on its own. Reporting success
                    // because the candidate list came back empty would tell the
                    // operator the house account is connected when it is not.
                    if (selectionToken) {
                        if (candidateAccounts?.length) {
                            setPendingSelection({
                                selectionToken,
                                candidateAccounts
                            });
                        } else {
                            setError(
                                t(
                                    'transaction_monitoring.connect_failed_or_cancelled'
                                )
                            );
                        }
                        return;
                    }

                    completeConnection();
                },
                onError: (mutationError) =>
                    backendErrorHandler('', () => {
                        setError(
                            t(
                                'transaction_monitoring.connect_failed_or_cancelled'
                            )
                        );
                    })(mutationError)
            }
        );
    }, [searchParams]);

    const handleConfirmSelection = (accountId: string) => {
        if (!pendingSelection) {
            return;
        }

        setSelectionError(undefined);
        selectHouseAccountAiiaAccount.mutate(
            {
                selectionToken: pendingSelection.selectionToken,
                accountId
            },
            {
                onSuccess: completeConnection,
                // These rejections are secret-free and tell the operator what to
                // do next ("already connected to another house account" versus
                // "selection expired, reconnect"), so the real message is shown
                // instead of a generic one. The picker stays up: a rejection
                // about one account need not apply to the others.
                onError: (mutationError) =>
                    backendErrorHandler(
                        t('transaction_monitoring.select_account_error'),
                        (_error, backendError) =>
                            setSelectionError(
                                backendError?.detail ??
                                    t(
                                        'transaction_monitoring.select_account_error'
                                    )
                            )
                    )(mutationError)
            }
        );
    };

    if (error) {
        return (
            <ContentWrapper>
                <Box textAlign="center">
                    <Alert severity="error">{error}</Alert>
                    <PrimaryButton
                        style={{ marginTop: '1rem' }}
                        onClick={() => navigate(MONITORING_PAGE_ROUTE)}
                    >
                        {t('transaction_monitoring.page_title')}
                    </PrimaryButton>
                </Box>
            </ContentWrapper>
        );
    }

    if (pendingSelection) {
        return (
            <ContentWrapper>
                <HouseAccountAiiaAccountPicker
                    accounts={pendingSelection.candidateAccounts}
                    isConfirming={selectHouseAccountAiiaAccount.isPending}
                    error={selectionError}
                    onConfirm={handleConfirmSelection}
                    onCancel={() => navigate(MONITORING_PAGE_ROUTE)}
                />
            </ContentWrapper>
        );
    }

    if (exchangeHouseAccountAiia.isPending) {
        return (
            <ContentWrapper>
                <HouseAccountAiiaAccountPickerSkeleton />
            </ContentWrapper>
        );
    }

    return null;
};

export default HouseAccountAiiaConnectCallbackPage;
